// public/js/shared/brand-qr.js — 福氣好門 QR 中心標章（Listing 追蹤與地圖分享共用）

export const DEFAULT_QR_LOGO_MODE = 'seal-four';

let qrLibPromise = null;

export function ensureQrLibrary() {
    if (typeof window !== 'undefined' && window.QRCode) return Promise.resolve();
    if (qrLibPromise) return qrLibPromise;
    qrLibPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = '/lib/qrcode.min.js';
        script.onload = () => resolve();
        script.onerror = () => {
            qrLibPromise = null;
            reject(new Error('無法載入本地 QR 產生套件'));
        };
        document.head.appendChild(script);
    });
    return qrLibPromise;
}

function drawRoundedRectPath(ctx, x, y, w, h, r) {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
}

function loadImageElement(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('載入自訂 Logo 圖片失敗'));
        img.src = src;
    });
}

async function drawCenterBadgeOnCanvas(ctx, mode, customDataUrl) {
    if (!mode || mode === 'none') return;
    if (mode === 'custom' && !customDataUrl) return;

    // 外圍白底護邊 (392..632, 240x240)
    ctx.save();
    drawRoundedRectPath(ctx, 392, 392, 240, 240, 52);
    ctx.fillStyle = '#ffffff';
    ctx.fill();

    if (mode === 'fu-gold') {
        // 款式 1：福字金印（官網同款 #F5BF6A 底 + #111A2E 深藏青字）
        drawRoundedRectPath(ctx, 404, 404, 216, 216, 44);
        ctx.fillStyle = '#F5BF6A';
        ctx.fill();
        ctx.lineWidth = 8;
        ctx.strokeStyle = '#111A2E';
        ctx.stroke();

        drawRoundedRectPath(ctx, 418, 418, 188, 188, 34);
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(17, 26, 46, 0.28)';
        ctx.stroke();

        ctx.fillStyle = '#111A2E';
        ctx.font = '900 124px Georgia, "Noto Serif TC", "Songti TC", "PMingLiU", serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('福', 512, 516);
    } else if (mode === 'seal-four') {
        // 款式 2：福氣好門雙排方印（#111A2E 底 + #F5BF6A 金字金框）
        drawRoundedRectPath(ctx, 404, 404, 216, 216, 38);
        ctx.fillStyle = '#111A2E';
        ctx.fill();
        ctx.lineWidth = 6;
        ctx.strokeStyle = '#F5BF6A';
        ctx.stroke();

        drawRoundedRectPath(ctx, 418, 418, 188, 188, 28);
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = 'rgba(245, 191, 106, 0.55)';
        ctx.stroke();

        ctx.fillStyle = '#F5BF6A';
        ctx.font = '800 60px "Noto Serif TC", "Songti TC", Georgia, serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('福氣', 512, 476);
        ctx.fillText('好門', 512, 552);
    } else if (mode === 'house-emblem') {
        // 款式 3：好門屋脊圖騰（松綠 #153f39 + 暖白屋身 + 赤陶門 #b9673e）
        drawRoundedRectPath(ctx, 404, 404, 216, 216, 46);
        ctx.fillStyle = '#153f39';
        ctx.fill();
        ctx.lineWidth = 6;
        ctx.strokeStyle = '#b9d3ca';
        ctx.stroke();

        ctx.save();
        ctx.translate(404, 404);
        ctx.scale(216 / 192, 216 / 192);

        // 屋身
        ctx.beginPath();
        ctx.moveTo(48, 78);
        ctx.lineTo(96, 42);
        ctx.lineTo(144, 78);
        ctx.lineTo(144, 152);
        ctx.lineTo(48, 152);
        ctx.closePath();
        ctx.fillStyle = '#f5f3ed';
        ctx.fill();

        // 好門（赤陶色門扇）
        ctx.fillStyle = '#b9673e';
        ctx.fillRect(77, 109, 38, 43);

        // 屋脊線條
        ctx.beginPath();
        ctx.moveTo(38, 82);
        ctx.lineTo(96, 37);
        ctx.lineTo(154, 82);
        ctx.strokeStyle = '#b9d3ca';
        ctx.lineWidth = 12;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();
        ctx.restore();
    } else if (mode === 'custom' && customDataUrl) {
        drawRoundedRectPath(ctx, 404, 404, 216, 216, 38);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.lineWidth = 4;
        ctx.strokeStyle = '#153f39';
        ctx.stroke();

        try {
            const img = await loadImageElement(customDataUrl);
            ctx.save();
            drawRoundedRectPath(ctx, 412, 412, 200, 200, 30);
            ctx.clip();
            const ratio = Math.min(200 / img.width, 200 / img.height);
            const drawW = img.width * ratio;
            const drawH = img.height * ratio;
            ctx.drawImage(img, 512 - drawW / 2, 512 - drawH / 2, drawW, drawH);
            ctx.restore();
        } catch {
            // ignore broken custom image
        }
    }
    ctx.restore();
}

function buildCenterBadgeSvg(mode, customDataUrl) {
    if (!mode || mode === 'none') return '';
    if (mode === 'custom' && !customDataUrl) return '';

    const whitePad = `<rect x="392" y="392" width="240" height="240" rx="52" fill="#ffffff"/>`;

    if (mode === 'fu-gold') {
        return [
            `<g shape-rendering="geometricPrecision">`,
            whitePad,
            `<rect x="404" y="404" width="216" height="216" rx="44" fill="#F5BF6A" stroke="#111A2E" stroke-width="8"/>`,
            `<rect x="418" y="418" width="188" height="188" rx="34" fill="none" stroke="#111A2E" stroke-opacity="0.28" stroke-width="3"/>`,
            `<text x="512" y="558" text-anchor="middle" fill="#111A2E" font-family="PingFang TC, Songti TC, 'Noto Serif TC', Georgia, serif" font-size="126" font-weight="900">福</text>`,
            `</g>`
        ].join('');
    }
    if (mode === 'seal-four') {
        return [
            `<g shape-rendering="geometricPrecision">`,
            whitePad,
            `<rect x="404" y="404" width="216" height="216" rx="38" fill="#111A2E" stroke="#F5BF6A" stroke-width="6"/>`,
            `<rect x="418" y="418" width="188" height="188" rx="28" fill="none" stroke="#F5BF6A" stroke-opacity="0.55" stroke-width="2.5"/>`,
            `<text x="512" y="498" text-anchor="middle" fill="#F5BF6A" font-family="PingFang TC, Songti TC, 'Noto Serif TC', Georgia, serif" font-size="60" font-weight="800" letter-spacing="4">福氣</text>`,
            `<text x="512" y="572" text-anchor="middle" fill="#F5BF6A" font-family="PingFang TC, Songti TC, 'Noto Serif TC', Georgia, serif" font-size="60" font-weight="800" letter-spacing="4">好門</text>`,
            `</g>`
        ].join('');
    }
    if (mode === 'house-emblem') {
        return [
            `<g shape-rendering="geometricPrecision">`,
            whitePad,
            `<rect x="404" y="404" width="216" height="216" rx="46" fill="#153f39" stroke="#b9d3ca" stroke-width="6"/>`,
            `<g transform="translate(404 404) scale(1.125)">`,
            `<path d="M48 78 96 42l48 36v67a7 7 0 0 1-7 7H55a7 7 0 0 1-7-7Z" fill="#f5f3ed"/>`,
            `<path d="M77 152v-43h38v43" fill="#b9673e"/>`,
            `<path d="M38 82 96 37l58 45" fill="none" stroke="#b9d3ca" stroke-width="12" stroke-linecap="round" stroke-linejoin="round"/>`,
            `</g>`,
            `</g>`
        ].join('');
    }
    if (mode === 'custom' && customDataUrl) {
        const safeHref = customDataUrl.replace(/"/g, '&quot;');
        return [
            `<g shape-rendering="geometricPrecision">`,
            whitePad,
            `<rect x="404" y="404" width="216" height="216" rx="38" fill="#ffffff" stroke="#153f39" stroke-width="4"/>`,
            `<image href="${safeHref}" x="412" y="412" width="200" height="200" preserveAspectRatio="xMidYMid meet"/>`,
            `</g>`
        ].join('');
    }
    return '';
}

/**
 * 使用本地 QRCode 矩陣繪製含標準 4 格留白區（Quiet Zone）與高容錯（Level H）中心標章。
 * 預設標章為福氣好門雙排方印 seal-four。
 * @param {string} text
 * @param {HTMLElement} holderEl
 * @param {{ logoMode?: string, customDataUrl?: string }} [options]
 */
export async function renderBrandedQr(text, holderEl, options = {}) {
    await ensureQrLibrary();
    holderEl.replaceChildren();

    const requested = options.logoMode || DEFAULT_QR_LOGO_MODE;
    const customDataUrl = options.customDataUrl || '';
    const activeLogoMode = requested === 'custom' && !customDataUrl
        ? DEFAULT_QR_LOGO_MODE
        : requested;
    const hasLogo = activeLogoMode !== 'none';

    const scratch = document.createElement('div');
    const instance = new window.QRCode(scratch, {
        text,
        width: 256,
        height: 256,
        correctLevel: hasLogo ? window.QRCode.CorrectLevel.H : window.QRCode.CorrectLevel.M
    });
    const model = instance._oQRCode;
    if (!model || typeof model.getModuleCount !== 'function') {
        holderEl.append(...scratch.childNodes);
        return { canvas: holderEl.querySelector('canvas'), svg: '' };
    }

    const count = model.getModuleCount();
    const margin = 4;
    const totalModules = count + margin * 2;
    const size = 1024;
    const scale = size / totalModules;

    const cutoutHalf = hasLogo ? Math.max(3, Math.floor(count * 0.105)) : -1;
    const centerMod = Math.floor(count / 2);
    const cutMin = centerMod - cutoutHalf;
    const cutMax = centerMod + cutoutHalf;

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = '#123832';

    const svgPaths = [];
    for (let r = 0; r < count; r += 1) {
        for (let c = 0; c < count; c += 1) {
            if (hasLogo && r >= cutMin && r <= cutMax && c >= cutMin && c <= cutMax) {
                continue;
            }
            if (!model.isDark(r, c)) continue;
            const x = Math.floor((c + margin) * scale);
            const y = Math.floor((r + margin) * scale);
            const w = Math.ceil((c + margin + 1) * scale) - x;
            const h = Math.ceil((r + margin + 1) * scale) - y;
            ctx.fillRect(x, y, w, h);
            svgPaths.push(`M${x},${y}h${w}v${h}h-${w}z`);
        }
    }

    await drawCenterBadgeOnCanvas(ctx, activeLogoMode, customDataUrl);
    const badgeSvg = buildCenterBadgeSvg(activeLogoMode, customDataUrl);

    const svg = [
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="1024" height="1024" shape-rendering="crispEdges">`,
        `<rect width="1024" height="1024" fill="#ffffff"/>`,
        `<path d="${svgPaths.join('')}" fill="#123832"/>`,
        badgeSvg,
        `</svg>`
    ].join('');

    holderEl.appendChild(canvas);
    return { canvas, svg };
}
